import { type KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useRef } from "react";

import type { ActivitySession } from "@learn-flow/contracts";

const deletionFailureMessage =
  "The session could not be deleted from your schedule. Nothing changed. Please try again.";

export function DeleteSessionDialog({
  session,
  pending,
  failed,
  onConfirm,
  onDismiss,
}: {
  session: ActivitySession;
  pending: boolean;
  failed: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  const headingId = useId();
  const descriptionId = useId();
  const label = "Session";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!dialog.open) dialog.showModal();
    keepButtonRef.current?.focus();
    return () => {
      if (dialog.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="delete-session-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      onCancel={(event) => {
        event.preventDefault();
        if (!pending) onDismiss();
      }}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef.current)}
    >
      <span className="section-kicker">Delete session</span>
      <h2 id={headingId}>Delete this session?</h2>
      <div className="delete-session-summary">
        <strong>{label}</strong>
        <span>{formatSessionSchedule(session)}</span>
      </div>
      <p id={descriptionId}>
        This session will be removed from your schedule. The activity and its session history will
        remain.
      </p>
      {failed ? (
        <p className="delete-session-error" role="alert">
          {deletionFailureMessage}
        </p>
      ) : null}
      <div className="delete-session-actions">
        <button
          ref={keepButtonRef}
          type="button"
          className="soft-button"
          disabled={pending}
          onClick={onDismiss}
        >
          Keep session
        </button>
        <button type="button" className="danger-button" disabled={pending} onClick={onConfirm}>
          {pending ? "Deleting…" : "Delete session"}
        </button>
      </div>
    </dialog>
  );
}

function formatSessionSchedule(session: ActivitySession): string {
  const [year, month, day] = session.scheduledDate.split("-").map(Number);
  const date =
    year && month && day
      ? new Intl.DateTimeFormat(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(year, month - 1, day)))
      : session.scheduledDate;
  if (!session.scheduledTime) return `${date} · Any time`;
  const [hour, minute] = session.scheduledTime.split(":").map(Number);
  const time =
    Number.isInteger(hour) && Number.isInteger(minute)
      ? new Intl.DateTimeFormat(undefined, {
          hour: "numeric",
          minute: "2-digit",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(2000, 0, 1, hour, minute)))
      : session.scheduledTime;
  return `${date} · ${time}`;
}

function trapDialogFocus(
  event: ReactKeyboardEvent<HTMLDialogElement>,
  dialog: HTMLDialogElement | null,
): void {
  if (event.key !== "Tab" || !dialog) return;
  const controls = [
    ...dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
